import { useState, useEffect } from 'react';

import { InvoiceForCurrencyForLegalEntity, Transaction, TransactionStatus } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Flyout, FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import dynamic from 'next/dynamic';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';

import { EmptyState } from '../../../../../shared/empty-state/empty-state.component';
import { usePaymentIntent } from '../../../hooks/use-payment-intent';
import { useStripeParams } from '../../../hooks/use-stripe-params';
import { OpenInvoiceFilters } from '../../../models/filters.model';
import { getInvoicesForPaymentType } from '../../../utils/invoice.utils';
import { OpenInvoicesNotFoundIllustration } from '../../empty-states/open-invoices-not-found-illustration';

import { InvoiceCollapsible } from './invoice-collapsible';

const InvoicePaymentFlyoutContent = dynamic(
   () => import('./payment-flyout/invoice-payment-flyout-content').then((mod) => mod.InvoicePaymentFlyoutContent),
   {
      ssr: false
   }
);

const createCollapsibleId = (legalEntity: string, currency: string) => `${legalEntity}.${currency}`;

const getFirstCollapsibleId = (legalEntityInvoices: InvoiceForCurrencyForLegalEntity[]) =>
   legalEntityInvoices.length === 1 && legalEntityInvoices[0].currencyTransactions.length === 1
      ? createCollapsibleId(legalEntityInvoices[0].legalEntity, legalEntityInvoices[0].currencyTransactions[0].currency)
      : null;

type Props = {
   openInvoices: InvoiceForCurrencyForLegalEntity[];
   openInvoiceFilters: OpenInvoiceFilters;
   canPayOnline: boolean;
   canDeductCreditNotes: boolean;
};

export const OpenInvoiceList = ({ openInvoices, openInvoiceFilters, canPayOnline, canDeductCreditNotes }: Props) => {
   const translate = useTranslations();

   const [expandedId, setExpandedId] = useQueryState('expanded', {
      defaultValue: getFirstCollapsibleId(openInvoices) ?? '',
      clearOnDefault: false
   });
   const [lastExpandedId, setLastExpandedId] = useState(() => getFirstCollapsibleId(openInvoices));

   const [selectedInvoiceIds, setSelectedInvoiceIds] = useQueryState('selectedInvoices', parseAsArrayOf(parseAsString, ',').withDefault([]));

   const { paymentIntent, isPending, isError, create: createPaymentIntent, clear: clearPaymentIntent } = usePaymentIntent();

   const { clear: clearStripeParams } = useStripeParams();

   const flyoutOpen = !!paymentIntent || isPending || isError;

   useEffect(() => {
      if (selectedInvoiceIds.length === 0) return;

      const allTransactions = openInvoices.flatMap((invoice) => invoice.currencyTransactions.flatMap((ct) => ct.transactions));

      const validIds = selectedInvoiceIds.filter((id) => {
         const transaction = allTransactions.find((t) => t.id === id);
         return transaction && transaction.status !== TransactionStatus.PAID && transaction.status !== TransactionStatus.PENDING;
      });

      if (validIds.length !== selectedInvoiceIds.length) {
         setSelectedInvoiceIds(validIds);
      }
   }, [openInvoices, selectedInvoiceIds, setSelectedInvoiceIds]);

   const handleExpandToggle = async (collapsibleId: string) => {
      if (collapsibleId === expandedId) {
         await setExpandedId('');
      } else {
         await setExpandedId(collapsibleId);
      }

      if (collapsibleId !== lastExpandedId) {
         await setSelectedInvoiceIds([]);
      }
      setLastExpandedId(collapsibleId);
   };

   const handlePay = (transactions: Transaction[], paymentType: 'selected' | 'open' | 'overdue') => {
      const invoiceIds = getInvoicesForPaymentType(transactions, selectedInvoiceIds, paymentType).map(({ id }) => id);
      createPaymentIntent(invoiceIds);
   };

   const handleFlyoutClose = async () => {
      await clearPaymentIntent();
      await clearStripeParams();
   };

   if (!openInvoices?.length) {
      return (
         <EmptyState
            label={translate('invoices.open-invoices.empty-state.label')}
            image={<OpenInvoicesNotFoundIllustration />}
            title={translate('invoices.open-invoices.empty-state.title')}
         />
      );
   }

   return (
      <>
         {openInvoices?.map((openInvoice) => (
            <Box key={openInvoice.legalEntity} direction="column">
               <Typography variant="body-highlight">{openInvoice.legalEntity}</Typography>
               {openInvoice.currencyTransactions.map((currencyTransaction) => {
                  if (currencyTransaction.transactions.length === 0) {
                     return null;
                  }

                  const collapsibleId = createCollapsibleId(openInvoice.legalEntity, currencyTransaction.currency);

                  return (
                     <InvoiceCollapsible
                        key={collapsibleId}
                        currency={currencyTransaction.currency}
                        openInvoices={currencyTransaction.transactions}
                        openInvoiceFilters={openInvoiceFilters}
                        selectedInvoiceIds={selectedInvoiceIds}
                        isExpanded={expandedId === collapsibleId}
                        canPayOnline={canPayOnline}
                        canDeductCreditNotes={canDeductCreditNotes}
                        onExpandToggle={() => handleExpandToggle(collapsibleId)}
                        onSelectedInvoiceIdsChange={(ids) => setSelectedInvoiceIds(ids)}
                        onPay={(type) => {
                           handlePay(currencyTransaction.transactions, type);
                        }}
                     />
                  );
               })}
            </Box>
         ))}

         {canPayOnline && (
            <Flyout open={flyoutOpen} onFlyoutClose={handleFlyoutClose}>
               <FlyoutHeader closeIcon="xmark" closeLabel="cancel">
                  {translate('invoices.payment.pay-open-amounts')}
               </FlyoutHeader>

               <InvoicePaymentFlyoutContent canDeductCreditNotes={canDeductCreditNotes} onClose={handleFlyoutClose} />
            </Flyout>
         )}
      </>
   );
};
